import { BASE_URL, pythonBaseUrl } from "../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../API_Endpoints/APIEndpoints";
import { v4 as uuidv4 } from "uuid";

/**
 * Service for handling upload extraction operations in parallel
 * This is specifically designed for bulk import processes
 */
class UploadExtractionService {
  constructor() {
    this.generateUUID = () => uuidv4();
  }

  /**
   * Process multiple files in parallel for bulk import
   * @param {Array} selectedFiles - Array of file objects to process
   * @param {Function} onProgressUpdate - Callback for progress updates
   * @returns {Promise<Array>} - Array of processing results
   */
  async processBulkFiles(selectedFiles, onProgressUpdate = null) {
    console.log(`🚀 Starting parallel processing of ${selectedFiles.length} files`);

    // Process all files in parallel
    const processPromises = selectedFiles.map(async (file, index) => {
      try {
        // Update progress callback if provided
        if (onProgressUpdate) {
          onProgressUpdate(file.fileImportId, "processing", `Processing ${file.fileName}`);
        }

        const result = await this.processIndividualFile(file);

        // Update progress on completion
        if (onProgressUpdate) {
          const status = result.success ? "completed" : "failed";
          const message = result.success
            ? `${file.fileName} processed successfully`
            : `${file.fileName} failed: ${result.error}`;
          onProgressUpdate(file.fileImportId, status, message);
        }

        return {
          ...result,
          fileId: file.fileImportId,
          fileName: file.fileName,
          index,
        };
      } catch (error) {
        console.error(`Error processing file ${file.fileName}:`, error);

        if (onProgressUpdate) {
          onProgressUpdate(
            file.fileImportId,
            "failed",
            `${file.fileName} failed: ${error.message}`
          );
        }

        return {
          success: false,
          error: error.message,
          fileId: file.fileImportId,
          fileName: file.fileName,
          index,
        };
      }
    });

    // Wait for all files to complete processing
    const results = await Promise.allSettled(processPromises);

    // Process results
    const processedResults = results.map((result, index) => {
      if (result.status === "fulfilled") {
        return result.value;
      } else {
        return {
          success: false,
          error: result.reason?.message || "Unknown error",
          fileId: selectedFiles[index]?.fileImportId || `file-${index}`,
          fileName: selectedFiles[index]?.fileName || `Unknown file ${index}`,
          index,
        };
      }
    });

    // Log summary
    const successful = processedResults.filter((r) => r.success);
    const failed = processedResults.filter((r) => !r.success);

    console.log(
      `📊 Bulk processing completed: ${successful.length} successful, ${failed.length} failed`
    );

    return processedResults;
  }

  /**
   * Process a single file
   * @param {Object} fileData - File data object
   * @returns {Promise<Object>} - Processing result
   */
  async processIndividualFile(fileData) {
    try {
      // Determine the processing type based on uploadType
      if (
        (fileData.uploadType >= 2 && fileData.uploadType <= 10) ||
        fileData.uploadType === 12 ||
        fileData.uploadType === 15
      ) {
        return await this.processSingleForm(fileData);
      } else if (fileData.uploadType === 11) {
        return await this.processPackFile(fileData);
      } else {
        return await this.processRegularFile(fileData);
      }
    } catch (error) {
      console.error(`Error in processIndividualFile for ${fileData.fileName}:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Upload and process a form added to an expanded forms pack.
   * The review screens only provide the selected file and parent row; all
   */
  async processAddedForm(file, selectedType, row) {
    const userData = getUserIdFromLocalStorage();
    const now = new Date();
    const pad = (value) => value.toString().padStart(2, "0");
    const dateStr = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
    const timeStr = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const baseName = `${selectedType.replace(/\s+/g, "_")}-${dateStr}-${timeStr}`;
    const filename = `${baseName}.pdf`;
    const fileId = this.generateUUID();
    const businessUnit = row.businessUnit || "Demo";
    const formattedType =
      selectedType === "Self-Cert Individual" || selectedType === "Self-Cert Entity"
        ? "SELF-CERT"
        : selectedType.replace(/-/g, "").toLowerCase();
    const blobPath = `${row.organizationId}/${row.userID}/${businessUnit}/split_documents/${row.fileImportId}/${filename}`;

    const uploadFormData = new FormData();
    uploadFormData.append("file", file);
    uploadFormData.append("filename", baseName);
    uploadFormData.append("original_filename", file.name);
    uploadFormData.append("organization_id", row.organizationId);
    uploadFormData.append("user_id", row.userID);
    uploadFormData.append("business_unit", businessUnit);
    uploadFormData.append("split_parent_id", row.fileImportId);
    await this.postOrThrow(`${pythonBaseUrl}/File/UploadFile`, uploadFormData, userData.token);

    const extractionFormData = new FormData();
    extractionFormData.append("filename", row.fileImportId);
    extractionFormData.append("filepath", blobPath);
    extractionFormData.append("original_filename", filename);
    extractionFormData.append("extension", "pdf");
    extractionFormData.append("doc_type", formattedType);
    extractionFormData.append("request_id", fileId);
    extractionFormData.append("correlation_id", fileId);
    extractionFormData.append("upload_type", "11");
    extractionFormData.append("is_single_form", "false");
    extractionFormData.append("organization_id", row.organizationId);
    extractionFormData.append("user_id", row.userID);
    extractionFormData.append("business_unit", businessUnit);
    const extractionResponse = await this.postOrThrow(
      `${pythonBaseUrl}/Extract/UploadExtraction`,
      extractionFormData,
      userData.token
    );
    const extractionData = await extractionResponse.json();
    const extractedForms = (
      Array.isArray(extractionData.data) ? extractionData.data : [extractionData.data]
    )
      .filter((formData) => formData && typeof formData === "object")
      .map((formData) => ({
        ...formData,
        // Keep the same blob-relative path used by UploadFile so the secure
        // viewer can resolve the added form from the parent pack row.
        viewPdf: blobPath,
        fileImportId: row.fileImportId,
        fileName: filename,
        filePath: "split_documents",
      }));

    const importFormData = new FormData();
    importFormData.append("filename", row.fileImportId);
    importFormData.append("request_id", row.fileImportId);
    importFormData.append("organization_id", row.organizationId);
    importFormData.append("user_id", row.userID);
    importFormData.append("business_unit", businessUnit);
    importFormData.append("original_filename", row.fileName);
    const importResponse = await this.postOrThrow(
      `${pythonBaseUrl}/Excel/Import-Excel-File`,
      importFormData,
      userData.token
    );
    const importedExcelData = await importResponse.json();

    return {
      formData: extractedForms,
      excelData: {
        extractedValidationData: importedExcelData.extractedValidationData,
        tableData: importedExcelData.tableData || this.parseStoredJson(row.dataExtraction, []),
        treeData: importedExcelData.treeData || this.parseStoredJson(row.dataVisualisation, []),
      },
      formsData: this.appendFormData(row.formsData, extractedForms),
      importResponse: importedExcelData,
    };
  }

  appendFormData(existingFormsData, addedFormData) {
    const addedForms = Array.isArray(addedFormData) ? addedFormData : [addedFormData];
    const validAddedForms = addedForms.filter((form) => form && typeof form === "object");
    if (validAddedForms.length === 0) return existingFormsData || JSON.stringify({ formsData: [] });

    const existing = this.parseStoredJson(existingFormsData, { formsData: [] });
    if (Array.isArray(existing)) {
      return JSON.stringify([...existing, ...validAddedForms]);
    }

    if (Array.isArray(existing.formsData)) {
      return JSON.stringify({
        ...existing,
        formsData: [...existing.formsData, { data: validAddedForms }],
      });
    }

    return JSON.stringify({ formsData: [{ data: validAddedForms }] });
  }

  mergeValidationData(existingValidation, importedValidation) {
    const existing =
      typeof existingValidation === "string"
        ? JSON.parse(existingValidation || "{}")
        : existingValidation || {};
    const imported = importedValidation || {};
    const merged = { ...existing, ...imported };

    Object.keys(existing).forEach((key) => {
      if (Array.isArray(existing[key])) {
        const importedRows = Array.isArray(imported[key]) ? imported[key] : [];
        const rows = [...existing[key], ...importedRows];
        const seen = new Set();
        merged[key] = rows.filter((row) => {
          const identity = JSON.stringify(row);
          if (seen.has(identity)) return false;
          seen.add(identity);
          return true;
        });
      }
    });

    return merged;
  }

  parseStoredJson(value, fallback) {
    if (!value) return fallback;
    if (typeof value !== "string") return value;
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }

  async postOrThrow(url, body, token) {
    const response = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body,
    });
    if (!response.ok) {
      let detail = "";
      try {
        const errorBody = await response.json();
        detail = errorBody.detail
          ? typeof errorBody.detail === "string"
            ? errorBody.detail
            : JSON.stringify(errorBody.detail)
          : errorBody.message || "";
      } catch {
        // Keep the HTTP status when the server does not return JSON.
      }
      throw new Error(
        `Request failed: ${response.status} ${response.statusText}${detail ? ` - ${detail}` : ""}`
      );
    }
    return response;
  }

  /**
   * Process single form files (uploadType 2-10)
   */
  async processSingleForm(fileData) {
    const docType = this.getDocumentTypeByUploadType(fileData.uploadType);

    const extractionFormData = new FormData();
    extractionFormData.append("filename", fileData.fileImportId);
    extractionFormData.append("request_id", fileData.fileImportId);
    extractionFormData.append("extension", fileData.fileName.split(".").pop());
    extractionFormData.append(
      "filepath",
      `${fileData.organizationId}/${fileData.userID}/uploadfiles/${fileData.fileImportId}.pdf`
    );
    extractionFormData.append("organization_id", fileData.organizationId);
    extractionFormData.append("original_filename", fileData.fileName);
    extractionFormData.append("user_id", fileData.userID);
    extractionFormData.append("business_unit", fileData.businessUnit || "Demo");
    extractionFormData.append("doc_type", docType);
    extractionFormData.append("is_single_form", "true");
    extractionFormData.append("upload_type", String(fileData.uploadType || 2));

    const extractionUrl = `${pythonBaseUrl}/Extract/UploadExtraction`;

    const extractionResponse = await fetch(extractionUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
      },
      body: extractionFormData,
      mode: "cors",
    });

    if (extractionResponse.ok) {
      const extractionData = await extractionResponse.json();

      if (extractionData.status === "success") {
        // Extract confidence score from response
        let confidenceLevel = null;
        if (extractionData.confidence_score !== undefined) {
          confidenceLevel =
            extractionData.confidence_score < 1
              ? Math.round(extractionData.confidence_score * 100)
              : Math.round(extractionData.confidence_score);
        } else if (extractionData.reportdata?.confidenceScore !== undefined) {
          confidenceLevel =
            extractionData.reportdata.confidenceScore < 1
              ? Math.round(extractionData.reportdata.confidenceScore * 100)
              : Math.round(extractionData.reportdata.confidenceScore);
        }

        // Only extract total pages and total forms if they don't already exist
        let totalPages = fileData.totalPages;
        let totalForms = fileData.totalForms;

        // Only update if current values are null/undefined
        if (extractionData.reportdata) {
          if (
            (totalPages === null || totalPages === undefined) &&
            extractionData.reportdata.totalPages !== undefined
          ) {
            totalPages = Number(extractionData.reportdata.totalPages);
          }
          if (
            (totalForms === null || totalForms === undefined) &&
            extractionData.reportdata.totalForms !== undefined
          ) {
            // For single forms, apply WHS logic: 0 for WHS, otherwise use response value
            totalForms = Number(extractionData.reportdata.totalForms);
          }
        } else if (extractionData.statistics) {
          // Fallback to statistics if reportdata not available (only if values are null/undefined)
          if (
            (totalPages === null || totalPages === undefined) &&
            extractionData.statistics.total_pages !== undefined
          ) {
            totalPages = Number(extractionData.statistics.total_pages);
          }
          if (
            (totalForms === null || totalForms === undefined) &&
            extractionData.statistics.total_forms !== undefined
          ) {
            totalForms = Number(extractionData.statistics.total_forms);
          }
        }

        const updatedRowData = {
          ...fileData,
          formsData: JSON.stringify(extractionData.data),
          isProcessed: true,
          confidenceLevel: confidenceLevel,
          totalPages: totalPages,
          totalForms: totalForms,
          status: 2, // Status 2: Extraction complete
        };

        const updateResponse = await fetch(`${BASE_URL}/FileData/ImportFiles`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
          },
          body: JSON.stringify(updatedRowData),
        });

        if (updateResponse.ok) {
          // Store extraction report data if available
          // Report data is now stored directly by Python - no need to store from React

          return {
            success: true,
            message: "Single Form extraction completed and saved successfully!",
            type: "single_form",
            confidenceLevel: confidenceLevel,
            totalPages: totalPages,
            totalForms: totalForms,
          };
        } else {
          throw new Error(`Failed to save to database. Status: ${updateResponse.status}`);
        }
      } else {
        throw new Error("Single Form extraction status failed");
      }
    } else {
      // Parse error response body to get specific error messages (e.g., malware detection, empty extraction)
      let errorMessage = "Single Form extraction upload failed";
      try {
        const errorData = await extractionResponse.json();
        if (errorData.detail?.message) {
          errorMessage = errorData.detail.message;
        } else if (errorData.detail?.error) {
          errorMessage = errorData.detail.error;
        } else if (errorData.error) {
          errorMessage = errorData.details || errorData.error;
        } else if (errorData.detail) {
          errorMessage =
            typeof errorData.detail === "string"
              ? errorData.detail
              : errorData.detail.message || "Extraction failed";
        }
      } catch {
        // Use default error message
      }
      throw new Error(errorMessage);
    }
  }

  /**
   * Process pack files (uploadType 11)
   */
  async processPackFile(fileData) {
    const formData = new FormData();
    formData.append("filename", fileData.fileImportId);
    formData.append("original_filename", fileData.fileName);
    formData.append("request_id", fileData.fileImportId);
    formData.append("extension", fileData.fileName.split(".").pop());
    formData.append("organization_id", fileData.organizationId);
    formData.append("user_id", fileData.userID);
    formData.append("business_unit", fileData.businessUnit || "Demo");
    formData.append("upload_type", String(fileData.uploadType || 11));

    const url = `${pythonBaseUrl}/Split/AzureSplitExtract`;

    const response = await fetch(url, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
      },
      mode: "cors",
    });

    if (response.ok) {
      const splitResponse = await response.json();

      if (splitResponse.status === "success") {
        // For bulk processing, we'll handle pack files differently
        // This is a simplified version - you may need to extend based on your requirements
        return {
          success: true,
          message: "Pack file processed successfully",
          type: "pack",
          data: splitResponse,
        };
      } else {
        throw new Error("Pack splitting failed");
      }
    } else {
      // Parse error response body to get specific error messages (e.g., malware detection)
      let errorMessage = "Pack file upload failed";
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.details || errorData.error;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        }
      } catch {
        // Use default error message
      }
      throw new Error(errorMessage);
    }
  }

  /**
   * Process regular files (uploadType 1)
   */
  async processRegularFile(fileData) {
    const formData = new FormData();
    formData.append("filename", fileData.fileImportId);
    formData.append("original_filename", fileData.fileName);
    formData.append("request_id", fileData.fileImportId);
    formData.append("extension", fileData.fileName.split(".").pop());
    formData.append("organization_id", fileData.organizationId);
    formData.append("user_id", fileData.userID);
    formData.append("business_unit", fileData.businessUnit || "Demo");
    formData.append("upload_type", String(fileData.uploadType || 1));

    const url = `${pythonBaseUrl}/Extract/Azure-Upload-File`;

    const response = await fetch(url, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
      },
      mode: "cors",
    });

    if (response.ok) {
      // For bulk processing, we might want to implement polling differently
      // This is a simplified version
      return {
        success: true,
        message: "Regular file uploaded successfully",
        type: "regular",
      };
    } else {
      // Parse error response body to get specific error messages (e.g., malware detection)
      let errorMessage = "Regular file upload failed";
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.details || errorData.error;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        }
      } catch {
        // Use default error message
      }
      throw new Error(errorMessage);
    }
  }

  /**
   * Get document type based on upload type
   */
  getDocumentTypeByUploadType(uploadType) {
    switch (uploadType) {
      case 1:
        return "WHS";
      case 2:
        return "W-9";
      case 3:
        return "W-8BEN";
      case 4:
        return "W-8BEN-E";
      case 5:
        return "W-8IMY";
      case 6:
        return "W-8ECI";
      case 7:
        return "W-8EXP";
      case 8:
        return "8233";
      case 9:
        return "SELFCERT-IND";
      case 10:
        return "SELFCERT-ENT";
      case 11:
        return "Withholding Pack";
      case 12:
        return "IDDOCUMENT";
      case 15:
        return "IDDOCUMENT";
      default:
        return null;
    }
  }

  /**
   * Store reports (if needed for bulk processing)
   */
  async storeReports(reportData) {
    try {
      const response = await fetch(`${BASE_URL}/FileImportReport/Report`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
          "Content-Type": "application/json",
          accept: "*/*",
        },
        body: JSON.stringify(reportData),
      });

      if (!response.ok) {
        throw new Error(`Failed with status: ${response.status}`);
      }

      // Try to parse as JSON, but handle plain text responses
      const contentType = response.headers.get("content-type");
      let data;

      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        // Backend returned plain text instead of JSON
        const textResponse = await response.text();
        data = { message: textResponse };
      }

      console.log("Report stored successfully:", data);
      return { success: true, data };
    } catch (error) {
      console.error("Failed to store report:", error);
      return { success: false, error };
    }
  }
}

export default UploadExtractionService;
